import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  CapstoneForLearner,
  ChatConfigResponse,
  EnrolledCourse,
  SessionDetail,
} from '@lp/shared';
import { api } from './api.js';
import type { CoursePageResponse, QuizAttemptHistoryItem } from './contracts.js';

/**
 * Every learner read, in one place, so a mutation can invalidate exactly the
 * query it changed instead of reaching for the whole cache.
 *
 * Keys follow the repo convention: an array whose first element names the
 * resource.
 */
export const learnerKeys = {
  courses: () => ['courses'] as const,
  course: (courseSlug: string) => ['course', courseSlug] as const,
  session: (courseSlug: string, sessionSlug: string) =>
    ['session', courseSlug, sessionSlug] as const,
  capstone: (courseSlug: string) => ['capstone', courseSlug] as const,
  quizAttempts: (sessionId: string) => ['quiz-attempts', sessionId] as const,
  chatConfig: () => ['chat', 'config'] as const,
};

export function useCourses(): UseQueryResult<EnrolledCourse[]> {
  return useQuery({
    queryKey: learnerKeys.courses(),
    queryFn: ({ signal }) => api.get<EnrolledCourse[]>('/courses', signal),
  });
}

export function useCoursePage(courseSlug: string): UseQueryResult<CoursePageResponse> {
  return useQuery({
    queryKey: learnerKeys.course(courseSlug),
    queryFn: ({ signal }) =>
      api.get<CoursePageResponse>(`/courses/${encodeURIComponent(courseSlug)}`, signal),
    enabled: courseSlug.length > 0,
  });
}

export function useSessionDetail(
  courseSlug: string,
  sessionSlug: string,
): UseQueryResult<SessionDetail> {
  return useQuery({
    queryKey: learnerKeys.session(courseSlug, sessionSlug),
    queryFn: ({ signal }) =>
      api.get<SessionDetail>(
        `/courses/${encodeURIComponent(courseSlug)}/sessions/${encodeURIComponent(sessionSlug)}`,
        signal,
      ),
    enabled: courseSlug.length > 0 && sessionSlug.length > 0,
    // The playback payload carries a short-TTL media token; refetching it on a
    // whim would hand hls.js a second token it never asked for.
    staleTime: 60_000,
  });
}

export function useCapstone(courseSlug: string): UseQueryResult<CapstoneForLearner> {
  return useQuery({
    queryKey: learnerKeys.capstone(courseSlug),
    queryFn: ({ signal }) =>
      api.get<CapstoneForLearner>(
        `/courses/${encodeURIComponent(courseSlug)}/capstone`,
        signal,
      ),
    enabled: courseSlug.length > 0,
  });
}

export function useQuizAttempts(
  sessionId: string,
  enabled: boolean,
): UseQueryResult<QuizAttemptHistoryItem[]> {
  return useQuery({
    queryKey: learnerKeys.quizAttempts(sessionId),
    queryFn: ({ signal }) =>
      api.get<QuizAttemptHistoryItem[]>(
        `/sessions/${encodeURIComponent(sessionId)}/quiz/attempts`,
        signal,
      ),
    enabled: enabled && sessionId.length > 0,
  });
}

export function useChatConfig(enabled: boolean): UseQueryResult<ChatConfigResponse> {
  return useQuery({
    queryKey: learnerKeys.chatConfig(),
    queryFn: ({ signal }) => api.get<ChatConfigResponse>('/chat/config', signal),
    enabled,
    staleTime: 10 * 60_000,
  });
}
